/**
 * Envio de arquivo em partes para PUT /upload/arquivos/:id.
 *
 * A borda aceita 64 MB por requisição; ZIP de um ano de notas passa disso. Cada
 * parte vai com o offset esperado. Se a conexão cair, o servidor responde 409 com
 * o que já recebeu e o envio retoma dali — sem recomeçar o arquivo.
 */

export const TAMANHO_PARTE = 8 * 1024 * 1024

export class ErroEnvio extends Error {}

export async function enviarEmPartes(
  arquivoId: string,
  arquivo: File,
  aoProgredir: (enviados: number) => void,
  sinal?: AbortSignal,
): Promise<void> {
  let offset = 0
  let falhasSeguidas = 0
  while (offset < arquivo.size) {
    if (sinal?.aborted) throw new ErroEnvio('Envio cancelado')
    const parte = arquivo.slice(offset, Math.min(offset + TAMANHO_PARTE, arquivo.size))
    let resposta: Response
    try {
      resposta = await fetch(`/upload/arquivos/${arquivoId}?offset=${offset}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/octet-stream' },
        body: parte,
        signal: sinal,
      })
    } catch {
      if (++falhasSeguidas > 5) throw new ErroEnvio('Conexão instável: o envio parou depois de 5 tentativas')
      await new Promise((r) => setTimeout(r, 1000 * falhasSeguidas))
      continue
    }
    const corpo = (await resposta.json().catch(() => ({}))) as { recebido?: number; erro?: string }
    if (resposta.status === 409 && typeof corpo.recebido === 'number') {
      offset = corpo.recebido // retoma de onde o servidor parou
      aoProgredir(offset)
      continue
    }
    if (!resposta.ok) {
      if (resposta.status >= 500 && ++falhasSeguidas <= 5) {
        await new Promise((r) => setTimeout(r, 1000 * falhasSeguidas))
        continue
      }
      throw new ErroEnvio(corpo.erro ?? `Falha no envio (HTTP ${resposta.status})`)
    }
    falhasSeguidas = 0
    offset = corpo.recebido ?? offset + parte.size
    aoProgredir(offset)
  }
}

/** Arquivos aceitos na seleção; o servidor ainda confere pelo conteúdo. */
export const aceito = (f: File) => /\.(xml|zip)$/i.test(f.name)

export function formatarBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1).replace('.', ',')} MB`
  return `${(n / 1024 ** 3).toFixed(2).replace('.', ',')} GB`
}
